import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Modal,
  Row,
  Spinner,
  Table
} from "react-bootstrap";

import { rockGemsApi } from "../services/rockGemsApi";
import { normalizePaginate } from "../utils/normalizePaginate";
import ErrorAlert from "../components/common/ErrorAlert";
import PaginationBar from "../components/common/PaginationBar";
import GemDetailModal from "../components/gems/GemDetailModal";
import GemLanguageSelect from "../components/gems/GemLanguageSelect";
import { gemTableLabel } from "../constants/gemLanguages";

function getGemUid(g) {
  return g?.uid || g?.id || g?.gem_uid;
}

function getGemTitle(g) {
  return (
    g?.preferred_name ||
    g?.latin_name ||
    g?.common_name ||
    g?.title ||
    g?.name ||
    g?.label ||
    getGemUid(g) ||
    "Gem"
  );
}

function getGemImage(g) {
  return g?.main_image_url || g?.main_image || g?.image_url || g?.image || g?.thumbnail || null;
}

function formatSource(source) {
  if (!source) return "—";
  if (source === "cms-cache") return "rock identifier";
  return source;
}

const ITEMS_PER_PAGE = 20;

export default function GemsListPage() {
  const [page, setPage] = useState(1);
  const [nameInput, setNameInput] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [languageCodeFilter, setLanguageCodeFilter] = useState("en");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [gems, setGems] = useState([]);
  const [meta, setMeta] = useState({});

  const [gemDetailUid, setGemDetailUid] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const payload = await rockGemsApi.getGemsList({
          name: nameFilter || undefined,
          source: sourceFilter || undefined,
          language_code: languageCodeFilter || "en",
          page,
          items_per_page: ITEMS_PER_PAGE
        });
        const normalized = normalizePaginate(payload);
        if (!cancelled) {
          setGems(normalized.items);
          setMeta(normalized.meta);
        }
      } catch (e) {
        if (!cancelled) setError(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, [page, nameFilter, sourceFilter, languageCodeFilter]);

  const pageSize = meta.items_per_page || ITEMS_PER_PAGE;

  const totalPages = useMemo(() => {
    if (typeof meta.total === "number" && pageSize > 0) {
      return Math.ceil(meta.total / pageSize);
    }
    return null;
  }, [meta.total, pageSize]);

  const canNext = useMemo(() => {
    if (totalPages !== null) return page < totalPages;
    return (gems || []).length >= pageSize;
  }, [totalPages, page, pageSize, gems]);

  function applyNameSearch(e) {
    e?.preventDefault?.();
    setPage(1);
    setNameFilter(nameInput.trim());
  }

  return (
    <div className="page">
      <div className="page-header d-print-none">
        <div className="container-xl">
          <div className="row g-2 align-items-center">
            <div className="col">
              <div className="page-pretitle">Explore</div>
              <h2 className="page-title">
                Gems
                <Badge bg="secondary" className="ms-2 fw-normal">
                  {gemTableLabel(languageCodeFilter)}
                </Badge>
              </h2>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body">
        <div className="container-xl">
          <ErrorAlert error={error} title="Failed to load gems" />

          {/* Filters */}
          <Card className="mb-3">
            <Card.Body>
              <Form onSubmit={applyNameSearch}>
                <Row className="g-2 align-items-end">
                  <Col xs={12} md>
                    <Form.Label className="small text-muted mb-1">Name / UID</Form.Label>
                    <InputGroup size="sm">
                      <Form.Control
                        placeholder="Filter by name or UID..."
                        value={nameInput}
                        onChange={(e) => setNameInput(e.target.value)}
                        aria-label="Gem name or UID filter"
                      />
                      <Button type="submit" variant="outline-secondary">
                        Search
                      </Button>
                      {nameFilter && (
                        <Button
                          variant="outline-danger"
                          onClick={() => {
                            setNameInput("");
                            setNameFilter("");
                            setPage(1);
                          }}
                        >
                          Clear
                        </Button>
                      )}
                    </InputGroup>
                  </Col>
                  <Col xs={12} md="auto">
                    <Form.Label className="small text-muted mb-1">Language</Form.Label>
                    <GemLanguageSelect
                      value={languageCodeFilter}
                      onChange={(code) => {
                        setLanguageCodeFilter(code);
                        setPage(1);
                      }}
                    />
                  </Col>
                  <Col xs={12} md="auto">
                    <Form.Label className="small text-muted mb-1">Source</Form.Label>
                    <Form.Select
                      size="sm"
                      value={sourceFilter}
                      onChange={(e) => {
                        setSourceFilter(e.target.value);
                        setPage(1);
                      }}
                    >
                      <option value="">All sources</option>
                      <option value="rock_identifier">rock_identifier</option>
                      <option value="openai">openai</option>
                      <option value="rockinapp">rockinapp</option>
                    </Form.Select>
                  </Col>
                </Row>
              </Form>
            </Card.Body>
          </Card>

          {/* Table */}
          <Card>
            <Card.Header className="py-2">
              <PaginationBar
                page={page}
                onPageChange={(p) => setPage(Math.max(1, p))}
                canPrev={page > 1}
                canNext={canNext}
                showPageLabel={false}
                totalPages={totalPages}
                totalItems={typeof meta.total === "number" ? meta.total : null}
              />
            </Card.Header>
            <Card.Body className="p-0">
              <div className="table-responsive">
                <Table hover className="table-vcenter card-table mb-0">
                  <thead>
                    <tr>
                      <th style={{ width: 64 }}>Image</th>
                      <th style={{ width: 220 }}>UID</th>
                      <th>Name</th>
                      <th style={{ width: 160 }}>Source</th>
                      <th style={{ width: 100 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={5} className="text-center py-4">
                          <Spinner animation="border" size="sm" className="me-2" />
                          Loading...
                        </td>
                      </tr>
                    ) : (gems || []).length === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center text-muted py-4">
                          No gems found.
                        </td>
                      </tr>
                    ) : (
                      (gems || []).map((g) => {
                        const uid = getGemUid(g);
                        const imgSrc = getGemImage(g);
                        return (
                          <tr key={uid || JSON.stringify(g)}>
                            <td>
                              {imgSrc ? (
                                <img
                                  src={imgSrc}
                                  alt={getGemTitle(g)}
                                  onClick={() => setPreviewImage({ src: imgSrc, title: getGemTitle(g) })}
                                  style={{
                                    width: 40,
                                    height: 40,
                                    objectFit: "cover",
                                    borderRadius: 4,
                                    border: "1px solid #dee2e6",
                                    cursor: "pointer"
                                  }}
                                />
                              ) : (
                                <div
                                  style={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: 4,
                                    border: "1px solid #dee2e6",
                                    background: "#f1f3f5",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: 18,
                                    color: "#adb5bd"
                                  }}
                                >
                                  💎
                                </div>
                              )}
                            </td>
                            <td className="text-muted small">{uid}</td>
                            <td className="fw-bold">{getGemTitle(g)}</td>
                            <td>
                              {g?.source ? (
                                <Badge bg={g.source === "cms-cache" ? "primary" : "secondary"}>
                                  {formatSource(g.source)}
                                </Badge>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>
                            <td className="text-end">
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => setGemDetailUid(uid)}
                              >
                                Detail
                              </Button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </Table>
              </div>
            </Card.Body>
            <Card.Footer className="py-2">
              <PaginationBar
                page={page}
                onPageChange={(p) => setPage(Math.max(1, p))}
                canPrev={page > 1}
                canNext={canNext}
                showPageLabel={false}
                totalPages={totalPages}
                totalItems={typeof meta.total === "number" ? meta.total : null}
              />
            </Card.Footer>
          </Card>
        </div>
      </div>

      <GemDetailModal
        show={!!gemDetailUid}
        onHide={() => setGemDetailUid(null)}
        uid={gemDetailUid}
        category_uid={undefined}
        language_code={languageCodeFilter}
      />

      <Modal show={!!previewImage} onHide={() => setPreviewImage(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title className="small">{previewImage?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0 text-center bg-dark">
          {previewImage && (
            <img
              src={previewImage.src}
              alt={previewImage.title}
              style={{ maxWidth: "100%", maxHeight: "80vh", objectFit: "contain" }}
            />
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}
